"use client"

import { useId, useState } from "react"
import { Input, Label } from "@ui/shad"
import { toast } from "sonner"

import { useCreateReportMutation } from "@/hooks/api/reports"
import { SimpleFormDialog } from "@/components/dialogs"

interface SaveAsReportDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  cohortId: string
  defaultName: string
}

export function SaveAsReportDialog({ open, setOpen, cohortId, defaultName }: SaveAsReportDialogProps) {
  const dialogFormId = useId()

  const [name, setName] = useState(defaultName)
  const createReport = useCreateReportMutation()

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setName(defaultName)
    }
    setOpen(newOpen)
  }

  const handleSave = () => {
    if (!name.trim()) return

    createReport.mutate(
      { name: name.trim(), source_cohort_id: cohortId },
      {
        onSuccess: () => {
          toast.success("Report saved")
          handleOpenChange(false)
        },
      }
    )
  }

  return (
    <SimpleFormDialog
      title={"Save as report"}
      open={open}
      onOpenChange={handleOpenChange}
      dismiss="explicit"
      action={{
        label: "Save report",
        form: dialogFormId,
        disabled: !name.trim() || createReport.isPending,
        pending: createReport.isPending,
      }}
      cancel={{ label: "Cancel", onClick: () => handleOpenChange(false), disabled: createReport.isPending }}
    >
      <form
        id={dialogFormId}
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (!name.trim() || createReport.isPending) return
          void handleSave()
        }}
      >
        <>
          <p>Save this cohort as a reusable report that can be rerun to generate fresh results.</p>
          <div>
            <Label htmlFor="report-name" className="text-sm font-normal">
              Report name
            </Label>
            <Input
              id="report-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Monthly client review"
              autoComplete="off"
              className="mt-1"
            />
          </div>
        </>
      </form>
    </SimpleFormDialog>
  )
}
